import type { FileDto } from '@/api/api.dto';

export type ContactType = 'phone' | 'email' | 'social';
export type ActivationStatus = 'registered' | 'invited' | 'active';

/**
 * The `people.person_roles` enum, in the order the migration declares it.
 * `vendor` is the service-provider role the backend calls `VesselServiceProvider`.
 */
export enum VesselRole {
  OWNER = 'owner',
  GUEST = 'guest',
  CAPTAIN = 'captain',
  FIRST_OFFICER = 'first_officer',
  YACHT_MANAGER = 'yacht_manager',
  CHEF = 'chef',
  ENGINEER = 'engineer',
  DECKHAND = 'deckhand',
  STEWARD = 'steward',
  VENDOR = 'vendor',
}

/**
 * Which subscription seat a person occupies on a service — the `seat_type` of their role
 * row, set through `PATCH /roles/seat`. One per tier for the billable seats and one for
 * the crew add-on; `harbor` has only the free seat.
 */
export enum SeatType {
  HARBOR_SEAT = 'harbor_seat',
  NAVIGATE_SEAT = 'navigate_seat',
  NAVIGATE_CREW = 'navigate_crew',
  COMMAND_SEAT = 'command_seat',
  COMMAND_CREW = 'command_crew',
}

export interface ContactDto {
  id: string;
  contact: string;
  contact_type: ContactType;
}

export interface PersonInfoDto {
  first_name: string;
  last_name: string;
  birthday?: string;
  gender?: string;
}

export interface PersonPreferencesDto {
  dietary_restrictions: string[];
  favorite_drinks: string[];
  favorite_foods: string[];
  room_preferences: string[];
  seasickness_notes: string;
}

/** `dto.PersonRolesResponse` — one entry per service the person belongs to. */
export interface PersonServiceDto {
  person_id: string;
  service_id: string;
  service_type: string;
  role: string;
  rights?: string[];
  presence?: string;
  join_date?: string;
  /** `omitempty` on a `*string`, so absent entirely for a person holding no seat. */
  seat_type?: string | null;
}

export interface PersonDto {
  id: string;
  activation_status: string;
  block_status: string;
  onboarded: boolean;
  avatar: FileDto | null;
  person_info: PersonInfoDto;
  contacts: ContactDto[];
  preferences: PersonPreferencesDto | null;
  /** Named `services` on the wire, not `roles`. */
  services: PersonServiceDto[];
  created_at: string;
  updated_at: string;
}

export interface PageDto<T> {
  count: number;
  items: T[];
}

export interface UpdatePersonRequestDto {
  first_name: string;
  last_name?: string;
  birthday?: string;
  gender?: string;
}

export interface NewContactDto {
  contact: string;
  contact_type: ContactType;
}

export interface AddContactsRequestDto {
  contacts: NewContactDto[];
}

export interface UpdateContactRequestDto {
  id: string;
  contact: string;
  contact_type: ContactType;
}

export interface ChangePreferencesRequestDto {
  preferences: PersonPreferencesDto;
}

export interface SetActivationStatusRequestDto {
  activation_status: ActivationStatus;
}

export interface Contact {
  id: string;
  contact: string;
  contactType: ContactType;
}

export interface PersonInfo {
  firstName: string;
  lastName: string;
  birthday?: string;
  gender?: string;
}

export interface PersonPreferences {
  dietaryRestrictions: string[];
  favoriteDrinks: string[];
  favoriteFoods: string[];
  roomPreferences: string[];
  seasicknessNotes: string;
}

/** The person's membership of one service: which vessel, and their role on it. */
export interface PersonService {
  personId: string;
  serviceId: string;
  serviceType: string;
  role: string;
  rights: string[];
  presence?: string;
  joinDate?: string;
  /** `null` when they hold no seat, or one this client doesn't recognise. */
  seatType: SeatType | null;
}

export interface Person {
  id: string;
  activationStatus: string;
  blockStatus: string;
  onboarded: boolean;
  avatar: string;
  personInfo: PersonInfo;
  contacts: Contact[];
  preferences: PersonPreferences | null;
  services: PersonService[];
  createdAt: string;
  updatedAt: string;
}

export interface PeoplePage {
  count: number;
  items: Person[];
}

export interface UpdatePersonInput {
  firstName: string;
  lastName?: string;
  birthday?: string;
  gender?: string;
}

export interface NewContact {
  contact: string;
  contactType: ContactType;
}

export interface UpdateContactInput {
  id: string;
  contact: string;
  contactType: ContactType;
}

export interface GetPeopleParams {
  serviceId: string;
  limit?: number;
  page?: number;
  search?: string;
}

export interface GetAllPeopleParams {
  limit?: number;
  page?: number;
  search?: string;
}

/**
 * `PATCH /roles/seat`. The service is named in the body *and* in `X-Service-Id`: the handler
 * reads the body, the seat-management middleware in front of it reads the header.
 */
export interface AssignSeatDto {
  person_id: string;
  service_id: string;
  seat_type: SeatType;
}

export interface AssignSeatInput {
  personId: string;
  serviceId: string;
  seatType: SeatType;
}

/**
 * `DELETE /roles/seat`. No seat type: the server frees whichever paid seat the person holds
 * by resetting it to `harbor_seat`, so there is nothing to name. Same two places for the
 * service as `AssignSeatDto`.
 */
export interface RemoveSeatDto {
  person_id: string;
  service_id: string;
}

export interface RemoveSeatInput {
  personId: string;
  serviceId: string;
}
